import type { Order } from '@commercelayer/sdk'
import type { Views } from '@integration-layer/schemas/GAEvents'
import {
  addToCartSchema,
  removeFromCartSchema,
  viewAbandonCartSchema,
  viewCartSchema,
} from '@integration-layer/schemas/GAEvents/cart'

/**
 * Represents the possible cart view types.
 * @typedef {('mini_cart'|'cart_page')} CartViews
 */
type CartViews = Extract<Views, 'mini_cart' | 'cart_page'>

/**
 * Parameters for viewing a cart event.
 * @typedef {Object} ViewCartParams
 * @property {CartViews} view - The type of cart view.
 * @property {Order} [cart] - Optional cart order details.
 */
type ViewCartParams = { view: CartViews; cart?: Order }

/**
 * Parameters for adding an item to cart event.
 * @typedef {Object} AddToCartParams
 * @property {Views} view - The view where the item is added.
 * @property {boolean} [addedFromChat=false] - Whether the item was added from chat.
 */
type AddToCartParams = { view: Views; addedFromChat?: boolean }

/**
 * Parameters for removing an item from cart event.
 * @typedef {Object} RemoveFromCartParams
 * @property {CartViews} view - The type of cart view.
 * @property {number} [qty] - Optional quantity to remove.
 * @property {boolean} [removedFromChat=false] - Whether the item was removed from chat.
 */
type RemoveFromCartParams = {
  view: CartViews
  qty?: number
  removedFromChat?: boolean
}

/**
 * Composable for handling Google Analytics cart-related events.
 * Provides methods to track cart interactions like view, add, remove, and abandon.
 *
 * @returns {Object} Cart event tracking methods and payload
 * @example
 * const {
 *   dispatchViewCartEvent,
 *   dispatchAddToCartEvent,
 *   dispatchRemoveFromCartEvent
 * } = useGACartEvents()
 */
export const useGACartEvents = () => {
  const debug = !!useRuntimeConfig().public.ga4.debug
  const { cartEventsPayload, getEcommerceData, setCartEventsPayload } =
    useGAEcommerceData()

  /**
   * Dispatches a view cart event to Google Analytics.
   * Tracks when a user views their cart on different views.
   *
   * @param {ViewCartParams} params - Cart view parameters
   * @param {Object} [options] - Additional event dispatch options
   * @param {boolean} [options.waitForDatalayerSetup=false] - Wait for data layer setup
   * @returns {Promise<void>}
   * @example
   * dispatchViewCartEvent({ view: 'cart_page', cart: myCart })
   */
  const dispatchViewCartEvent = async (
    { view, cart }: ViewCartParams,
    options?: { waitForDatalayerSetup?: boolean }
  ) => {
    if (cart) setCartEventsPayload({ cart })
    const ecommerce = await getEcommerceData(false)
    const showItemList = computed(() =>
      ecommerce?.items.length === 1 ? ecommerce.items[0].item_list_name : ''
    )

    if (!ecommerce) return
    useGASendEvent(
      viewCartSchema,
      {
        event: 'view_cart',
        eventID: '006',
        ecommerce: {
          item_list_id: '',
          item_list_name: showItemList.value,
          view_type: view,
          ...ecommerce,
        },
      },
      {
        clearEcommerce: true,
        debug,
        waitForDatalayerSetup: options?.waitForDatalayerSetup,
      }
    )
  }

  /**
   * Dispatches an add to cart event to Google Analytics.
   * Tracks when a user adds an item to their cart.
   *
   * @param {AddToCartParams} params - Add to cart parameters
   * @returns {Promise<void>}
   * @example
   * dispatchAddToCartEvent({ view: 'product_page', addedFromChat: false })
   */
  const dispatchAddToCartEvent = async ({
    view,
    addedFromChat = false,
  }: AddToCartParams) => {
    const ecommerce = await getEcommerceData(addedFromChat, 1)
    if (!ecommerce) return
    useGASendEvent(
      addToCartSchema,
      {
        event: 'add_to_cart',
        eventID: '007',
        ecommerce: {
          view_type: view,
          ...ecommerce,
        },
      },
      { clearEcommerce: true, debug }
    )
    cartEventsPayload.value = null
    await dispatchAbandonCartEvent(true)
  }

  /**
   * Dispatches a remove from cart event to Google Analytics.
   * Tracks when a user removes an item from their cart.
   *
   * @param {RemoveFromCartParams} params - Remove from cart parameters
   * @returns {Promise<void>}
   * @example
   * dispatchRemoveFromCartEvent({ view: 'cart_page', qty: 1 })
   */
  const dispatchRemoveFromCartEvent = async ({
    view,
    qty,
    removedFromChat = false,
  }: RemoveFromCartParams) => {
    const ecommerce = await getEcommerceData(removedFromChat, qty)
    if (!ecommerce) return
    useGASendEvent(
      removeFromCartSchema,
      {
        event: 'remove_from_cart',
        eventID: '008',
        ecommerce: {
          view_type: view,
          ...ecommerce,
        },
      },
      { clearEcommerce: true, debug }
    )
    cartEventsPayload.value = null
    await dispatchAbandonCartEvent(true)
  }

  /**
   * Dispatches an abandon cart event to Google Analytics.
   * Tracks when a cart is potentially abandoned by the user.
   *
   * @param {boolean} [updateCart=false] - Whether to update cart before dispatching
   * @returns {Promise<void>}
   * @example
   * dispatchAbandonCartEvent(true)
   */
  const dispatchAbandonCartEvent = async (updateCart?: boolean) => {
    const { cart } = useCart()
    if (cart?.value && updateCart) setCartEventsPayload({ cart: cart.value })
    const ecommerce = await getEcommerceData()
    if (!ecommerce) return
    useGASendEvent(viewAbandonCartSchema, {
      event: 'view_abandon_cart',
      ecommerce,
    })
  }

  return {
    cartEventsPayload,
    setCartEventsPayload,
    dispatchViewCartEvent,
    dispatchAddToCartEvent,
    dispatchRemoveFromCartEvent,
    dispatchAbandonCartEvent,
  }
}
